export default function Terms() {
  return (
    <div style={{ minHeight: "100vh", padding: "60px 24px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <h1 style={h1}>Terms of Service</h1>
        <p style={meta}>Effective date: May 25, 2025</p>

        <Section title="1. Acceptance of Terms">
          <p style={body}>
            By accessing or using WorldRTT ("Service"), you agree to be bound by
            these Terms of Service ("Terms"). If you do not agree, do not use
            the Service. These Terms apply to all visitors, users, and others
            who access or use the Service.
          </p>
        </Section>

        <Section title="2. About the Service">
          <p style={body}>
            WorldRTT is a network latency measurement tool that allows users to
            probe HTTP/HTTPS endpoints from multiple geographic regions and view
            round-trip time (RTT) results. The Service is operated as a personal
            project by an individual based in British Columbia, Canada. The
            Service is offered in a free tier and a paid Pro tier with expanded
            capabilities.
          </p>
        </Section>

        <Section title="3. Accounts">
          <p style={body}>
            You must create an account to access certain features. You are
            responsible for maintaining the confidentiality of your login
            credentials and for all activity that occurs under your account. You
            must provide accurate information and keep it current. You must be
            at least 13 years old, or the minimum age of digital consent in your
            jurisdiction, to use the Service.
          </p>
        </Section>

        <Section title="4. Acceptable Use">
          <p style={body}>You agree not to use the Service to:</p>
          <ul style={list}>
            <li>
              Probe endpoints you do not own or have explicit permission to test
            </li>
            <li>
              Conduct or facilitate denial-of-service attacks or resource
              exhaustion against any system
            </li>
            <li>
              Probe private IP ranges, localhost addresses, or cloud provider
              metadata endpoints
            </li>
            <li>Circumvent rate limits or other technical restrictions</li>
            <li>Violate any applicable law or regulation</li>
            <li>
              Resell or redistribute probe capacity without prior written
              permission
            </li>
          </ul>
          <p style={body}>
            We reserve the right to suspend or terminate accounts that violate
            these restrictions, without notice or refund.
          </p>
        </Section>

        <Section title="5. Payments and Subscriptions">
          <p style={body}>
            Pro tier subscriptions are billed monthly or annually via our
            payment processor. By subscribing, you authorize us to charge your
            payment method on a recurring basis. Subscriptions automatically
            renew unless cancelled before the renewal date via the customer
            portal.
          </p>
          <p style={body}>
            If you believe you have been billed in error, contact us at{" "}
            <a href="mailto:legal@worldrtt.com" style={link}>
              legal@worldrtt.com
            </a>{" "}
            within 7 days of the charge. Refunds are otherwise issued at our
            discretion. Where required by law (including applicable Canadian
            consumer protection legislation), statutory refund rights are not
            affected by these Terms.
          </p>
        </Section>

        <Section title="6. Free Tier">
          <p style={body}>
            Free tier users are subject to rate limits on probe volume and
            frequency. WorldRTT reserves the right to adjust free tier limits
            at any time with reasonable notice.
          </p>
        </Section>

        <Section title="7. Intellectual Property">
          <p style={body}>
            The Service and its original content, features, and functionality
            are the property of WorldRTT and are protected by applicable
            intellectual property laws. You retain ownership of any data you
            submit through the Service.
          </p>
        </Section>

        <Section title="8. Probe Data and Shared Results">
          <p style={body}>
            Probe results are stored and associated with your account. Anonymous
            probe data is stored temporarily for rate-limiting purposes and
            deleted within 24 hours. Shared result links are publicly accessible
            to anyone with the URL. You are responsible for any data you choose
            to make public via share links.
          </p>
        </Section>

        <Section title="9. Disclaimer of Warranties">
          <p style={body}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
            LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE THAT THE SERVICE
            WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT RESULTS WILL BE ACCURATE
            OR COMPLETE.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p style={body}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WORLDRTT SHALL
            NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
            OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
            INCURRED DIRECTLY OR INDIRECTLY, ARISING FROM YOUR USE OF OR
            INABILITY TO USE THE SERVICE.
          </p>
        </Section>

        <Section title="11. Termination">
          <p style={body}>
            We may suspend or terminate your access to the Service at any time
            for violation of these Terms. You may cancel your account at any
            time via the settings page. Upon termination, your right to use the
            Service ceases immediately. Subscription fees already paid are
            non-refundable except as required by applicable law.
          </p>
        </Section>

        <Section title="12. Changes to These Terms">
          <p style={body}>
            We reserve the right to update these Terms at any time. We will
            notify you of material changes via email or a notice on the Service.
            Continued use of the Service after changes take effect constitutes
            acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="13. Governing Law">
          <p style={body}>
            These Terms are governed by and construed in accordance with the
            laws of the Province of British Columbia and the federal laws of
            Canada applicable therein, without regard to conflict of law
            principles. Any disputes arising under these Terms shall be subject
            to the exclusive jurisdiction of the courts of British Columbia,
            Canada.
          </p>
        </Section>

        <Section title="14. Contact">
          <p style={body}>
            Questions about these Terms? Contact us at{" "}
            <a href="mailto:legal@worldrtt.com" style={link}>
              legal@worldrtt.com
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="glass" style={{ padding: "24px 28px", marginBottom: 16 }}>
      <h2 style={h2}>{title}</h2>
      {children}
    </div>
  );
}

const h1: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 700,
  color: "#EFF2F5",
  margin: "0 0 8px",
};
const meta: React.CSSProperties = {
  color: "#98A1AB",
  fontSize: 14,
  margin: "0 0 32px",
};
const h2: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "#FF2566",
  margin: "0 0 12px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const body: React.CSSProperties = {
  color: "#EFF2F5",
  fontSize: 15,
  lineHeight: 1.7,
  margin: "0 0 10px",
};
const list: React.CSSProperties = {
  color: "#EFF2F5",
  fontSize: 15,
  lineHeight: 1.9,
  paddingLeft: 20,
  margin: "0 0 10px",
};
const link: React.CSSProperties = {
  color: "#FF2566",
  textDecoration: "none",
};
